import React, {useEffect, useImperativeHandle, useRef, useState, useContext} from 'react';
import {useLocation} from 'react-router-dom';
import {Mask} from '@tboerc/maskfy';
import {useFormik} from 'formik';
import * as Yup from 'yup';
import {isMobile} from 'react-device-detect';
import dayjs from 'dayjs';
import {api, Context} from '../../../../services';

import {isEmptyObject} from '../../../../helpers';
import {Input} from '../../../../components/atoms';
import {Select} from '../../../../components/molecules';
import Icons from '../../../../assets/svgs/icons';
import {strings} from '../../../../assets';

const CALLER = 'BIKE';

const INITIAL_VALUES = {
  numero_serie: '',
  modelo: '',
  id_fabricante: '',
  fabricante_modal: '',
  valor: '',
  categoria: 'convencional',
  lmi_contratado: '100',
  id_modalidade_bicicleta: '',
  ano: '',
  nota_fiscal: '',
  id_potencia: '',
  id_modelo: '',
};

const Plano = ({position, current, checkAdvance, extraData, prevData, corPrimaria}) => {
  const location = useLocation();
  const corretor = location.search;
  const isCostumer = !!corretor;
  const sizeRef = useRef();
  const [authState] = useContext(Context);

  const [modelosCiclomotores, setModelosCiclomotores] = useState([]);
  const [potenciassCiclomotores, setPotenciasCiclomotores] = useState([]);

  useImperativeHandle(ref, () => ({
    ref: sizeRef,
    values: () => ({caller: CALLER, data: {segurado_bicicleta: values}}),
  }));

  const validationSchema = Yup.object().shape({
    numero_serie: Yup.string().required(strings.required),
    nota_fiscal: Yup.number('Somente números com até 19 caracteres.')
      .required('Somente números com até 19 caracteres.')
      .test('regex', 'Somente números com até 19 caracteres.', (val) => {
        let regExp = new RegExp(/^\d{1,19}$/);

        return regExp.test(val);
      }),

    modelo: prevData.modal === 3 ? Yup.string() : Yup.string().required(strings.required),
    valor:
      prevData.modal === 2
        ? Yup.string()
            .required(strings.required)
            .test(
              'vd_valor',
              'Valor fora do permitido para o Bici Sura Essencial, realize uma cotação no fluxo Bici Sura',
              (val) => {
                const raw = Mask.money.raw(val);
                if (raw > 5000 || raw < 1000) return false;
                return true;
              },
            )
        : Yup.string()
            .required(strings.required)
            .test('vd_valor', 'Valor acima do permitido, favor acionar seu gerente comercial', (val) => {
              const raw = Mask.money.raw(val);
              if (raw > 150000) return false;
              return true;
            }),
    categoria: Yup.string().required(strings.required),
    lmi_contratado: Yup.string().required(strings.required),
    id_modalidade_bicicleta: Yup.number()
      .test('MOD', 'A modalidade Scooter não é permitida nesse fluxo, realize uma cotação no fluxo E-Moto Sura', (mod) => {
        if (mod === 6 && prevData.modal !== 3) return false;
        return true;
      })
      .required(strings.required),
    ano:
      prevData.modal === 2
        ? Yup.string()
            .test('DOB', 'Bici Sura Essencial aceita no máximo 3 anos', (value) => {
              if (+value <= dayjs().year() && dayjs().diff(dayjs(value), 'years') <= 3 && value.length === 4) {
                return true;
              }
            })
            .required(strings.required)
        : prevData.modal === 3
        ? Yup.string()
            .test('DOB', 'E-Moto Sura aceita no máximo 2 anos', (value) => {
              if (+value <= dayjs().year() && dayjs().diff(dayjs(value), 'years') <= 2 && value.length === 4) {
                return true;
              }
            })
            .required(strings.required)
        : Yup.string()
            .test('DOB', 'Preencha corretamente', (value) => {
              if (+value <= dayjs().year() && dayjs().diff(dayjs(value), 'years') < 100 && value.length === 4) {
                return true;
              }
            })
            .required(strings.required),
  });

  const {handleChange, handleBlur, errors, touched, values, setFieldValue, setFieldTouched, setValues} = useFormik({
    initialValues:
      prevData.modal === 0 || prevData.modal === 2 || !prevData.modal
        ? INITIAL_VALUES
        : prevData.modal === 3
        ? {...INITIAL_VALUES, id_modalidade_bicicleta: 6, categoria: 'eletrica'}
        : {...INITIAL_VALUES, categoria: 'eletrica'},
    validationSchema: validationSchema,
    initialErrors: strings.INITIAL_ERRORS,
  });

  const nota_fiscal = useRef();
  const serial = useRef();
  const model = useRef();
  const value = useRef();
  const ano = useRef();

  useEffect(() => {
    if (current === position) {
      checkAdvance({caller: CALLER, status: isEmptyObject(errors)});
    }
  });

  useEffect(() => {
    if (current === position) {
      if (prevData.isUpdatingEquipment) {
        setValues({
          numero_serie: prevData.segurado_bicicleta.numero_serie,
          modelo: prevData.segurado_bicicleta.modelo,
          fabricante_modal: prevData.segurado_bicicleta.fabricante_modal,
          valor: prevData.segurado_bicicleta.valor,
          id_modalidade_bicicleta: prevData.segurado_bicicleta.id_modalidade_bicicleta,
          id_fabricante: prevData.segurado_bicicleta.id_fabricante,
          categoria: prevData.segurado_bicicleta.categoria,
          ano: prevData.segurado_bicicleta.ano,
          nota_fiscal: prevData.segurado_bicicleta.nota_fiscal,
          id_potencia: prevData.segurado_bicicleta.id_potencia,
          id_modelo: prevData.segurado_bicicleta.id_modelo,
          lmi_contratado: prevData.segurado_bicicleta.lmi_contratado,
        });
      } else {
        setValues(
          prevData.modal === 3
            ? {
                numero_serie: '',
                modelo: '',
                fabricante_modal: '',
                valor: '',
                categoria: 'convencional',
                lmi_contratado: '100',
                ano: '',
                nota_fiscal: '',
                id_potencia: '',
                id_modelo: '',
                id_fabricante: '',
                id_modalidade_bicicleta: 6,
              }
            : {
                numero_serie: '',
                modelo: '',
                fabricante_modal: '',
                valor: '',
                categoria: 'convencional',
                lmi_contratado: '100',
                ano: '',
                nota_fiscal: '',
                id_potencia: '',
                id_modelo: '',
                id_fabricante: '',
                id_modalidade_bicicleta: '',
              },
        );
      }
    }
  }, [current, position, setValues]);

  useEffect(() => {
    setValues({...values, lmi_contratado: '100'});
  }, [values.valor]);

  useEffect(() => {
    const busca_modelos = async () => {
      try {
        if (values.id_fabricante && values.id_fabricante !== '') {
          const {data: resp} = await api.get(`modelo_ciclomotores?id_fabricante=${values.id_fabricante}&status=1`, {
            headers: {authorization: authState.token},
          });

          setModelosCiclomotores(resp.data);
        }
      } catch (error) {
        console.log(error);
      }
    };

    busca_modelos();
  }, [values.id_fabricante]);

  useEffect(() => {
    const busca_potencias = async () => {
      try {
        console.log(values.id_modelo);
        if (values.id_modelo && values.id_modelo !== '') {
          const {data: resp} = await api.get(`potencia_ciclomotores?id_modelo=${values.id_modelo}`, {
            headers: {authorization: authState.token},
          });
          console.log(resp);
          setPotenciasCiclomotores(resp.data);
        }
      } catch (error) {
        console.log(error);
      }
    };

    busca_potencias();
  }, [values.id_modelo]);

  const handleFabricante = (e) => {
    setModelosCiclomotores([]);
    setPotenciasCiclomotores([]);
    setValues({
      ...values,
      id_fabricante: e,
      id_modelo: '',
      id_potencia: '',
      valor: '',
      lmi_contratado: '100',
    });
  };

  const handleModelo = (e) => {
    setPotenciasCiclomotores([]);
    setValues({
      ...values,
      id_modelo: e,
      id_potencia: '',
      valor: '',
      lmi_contratado: '100',
    });
  };

  const handlePotencia = (e) => {
    setValues({
      ...values,
      id_potencia: e,
      valor: Mask.money.value(parseFloat(potenciassCiclomotores.filter((m) => m.id === e)[0].valor_referencia).toFixed(2)),
      lmi_contratado: '100',
    });
  };

  return (
    <form className="formStep" ref={sizeRef} style={{height: isMobile ? 'auto' : '100%'}}>
      <div className="controlForms">
        <div className="leftSide">
          {prevData.modal === 0 || prevData.modal === 2 ? (
            <h1 style={{color: !!corPrimaria ? corPrimaria : '#023a86'}}>Dados da bicicleta</h1>
          ) : (
            <h1 style={{color: !!corPrimaria ? corPrimaria : '#023a86'}}>Dados do equipamento</h1>
          )}

          {isCostumer ? (
            <p>
              {prevData.modal === 0 || prevData.modal === 2
                ? 'Insira os dados de sua bicicleta para continuar o cadastro e cotação do seguro. Lembrando que somente bicicletas que possuam Nota Fiscal Eletrônica serão passiveis de aceitação do seguro.'
                : 'Insira os dados do seu equipamento para continuar o cadastro e cotação do seguro. Lembrando que somente equipamentos que possuam Nota Fiscal Eletrônica serão passiveis de aceitação do seguro.'}
            </p>
          ) : (
            <p>
              {prevData.modal === 0 || prevData.modal === 2
                ? 'Insira os dados da bicicleta do cliente para continuar com o cadastro. Esses dados não devem ser divulgados.'
                : 'Insira os dados do equipamento do cliente para continuar com o cadastro. Esses dados não devem ser divulgados.'}
            </p>
          )}
        </div>

        <div className="rightSide">
          <div className="formGroup">
            <div className="group">
              {prevData === 0 ? (
                <label>
                  Número de série <span>(normalmente gravado e localizado embaixo do quadro, próximo ao pedivela)</span>
                </label>
              ) : (
                <label>Número de série</label>
              )}

              <Input
                ref={serial}
                nextTabRef={ano}
                placeholder="0000 0000 0000 0000"
                name="numero_serie"
                value={values.numero_serie}
                onChangeText={handleChange('numero_serie')}
                onBlur={handleBlur('numero_serie')}
                error={errors.numero_serie && touched.numero_serie}
              />
              {errors?.numero_serie && <small className={'text-danger errorInput'}>{errors?.numero_serie}</small>}
            </div>

            <div className="especialGroup">
              <div className="especialFields">
                <div className="fields">
                  <label className="especialLabel">Ano de fabricação</label>
                  <Input
                    ref={ano}
                    placeholder="Ex. 2020"
                    nextTabRef={nota_fiscal}
                    name="number"
                    inputMode="numeric"
                    value={values.ano}
                    type="number"
                    onChangeText={handleChange('ano')}
                    onBlur={handleBlur('ano')}
                    formatText={(e) => Mask.custom.value(e, '9999')}
                    error={errors.ano && touched.ano}
                  />
                  {errors?.ano && <small className={'text-danger errorInput'}>{errors?.ano}</small>}
                </div>

                <div className="fields">
                  <label className="especialLabel">Nota Fiscal</label>
                  <Input
                    ref={nota_fiscal}
                    placeholder="Nota Fiscal"
                    nextTabRef={model}
                    name="nota_fiscal"
                    type="number"
                    inputMode="numeric"
                    value={values.nota_fiscal}
                    onChangeText={handleChange('nota_fiscal')}
                    onBlur={handleBlur('nota_fiscal')}
                    error={errors.nota_fiscal && touched.nota_fiscal}
                  />
                  {errors?.nota_fiscal && <small className={'text-danger errorInput'}>{errors?.nota_fiscal}</small>}
                </div>
              </div>
            </div>

            <div className="especialGroup">
              <div className="especialFields">
                <div className="fields">
                  <label className="especialLabel">Modalidade</label>
                  <Select
                    value={values.id_modalidade_bicicleta}
                    disabled={prevData.modal === 3}
                    selected={
                      values.id_modalidade_bicicleta === ''
                        ? null
                        : extraData.modalidades
                            .filter((m) => m.id === values.id_modalidade_bicicleta)
                            .map((item) => ({...item, label: item.tipo, value: item.id}))?.[0]
                    }
                    tabIndex={current === position ? 2 : -1}
                    options={extraData.modalidades
                      .filter((item) => (prevData.modal === 0 || prevData.modal === 2 ? item.id <= 5 : item.id >= 6))
                      .map((item) => ({...item, label: item.tipo, value: item.id}))}
                    placeholder={'Clique para selecionar a modalidade'}
                    onBlur={() => setFieldTouched('id_modalidade_bicicleta', true, false)}
                    onChange={({value}) => {
                      console.log(value);
                      setFieldValue('id_modalidade_bicicleta', value, true);
                    }}
                  />
                  {errors?.id_modalidade_bicicleta && (
                    <small className={'text-danger errorInput'}>{errors?.id_modalidade_bicicleta}</small>
                  )}
                </div>

                {prevData.modal === 0 || prevData.modal === 2 || prevData.modal === 3 ? (
                  <div className="fields">
                    <label className="especialLabel">Fabricante</label>
                    <Select
                      value={values.id_fabricante}
                      selected={
                        values.id_fabricante === ''
                          ? null
                          : extraData.fabricantes
                              .filter((m) => m.id === values.id_fabricante)
                              .map((item) => ({...item, label: item.nome, value: item.id}))?.[0]
                      }
                      tabIndex={current === position ? 3 : -1}
                      options={extraData.fabricantes
                        .filter(({eh_ciclomotor}) => (prevData.modal === 3 ? eh_ciclomotor === 1 : eh_ciclomotor === 0))
                        .map((item) => ({...item, label: item.nome, value: item.id}))}
                      placeholder="Clique para selecionar o fabricante"
                      onBlur={() => setFieldTouched('id_fabricante', true, false)}
                      onChange={({value}) => handleFabricante(value)}
                    />
                    {errors?.id_fabricante && <small className={'text-danger errorInput'}>{errors?.id_fabricante}</small>}
                  </div>
                ) : (
                  <div className="fields">
                    <label className="especialLabel">Fabricante</label>
                    <Input
                      tabIndex={-1}
                      nextTabRef={model}
                      placeholder={'Qual é o fabricante do seu equipamento?'}
                      name="fabricante_modal"
                      value={values.fabricante_modal}
                      onChangeText={handleChange('fabricante_modal')}
                      onBlur={handleBlur('fabricante_modal')}
                      error={errors.fabricante_modal && touched.fabricante_modal}
                    />
                    {errors?.fabricante_modal && <small className={'text-danger errorInput'}>{errors?.fabricante_modal}</small>}
                  </div>
                )}
              </div>
            </div>
            <div className={prevData.modal === 3 ? 'especialGroup' : ''}>
              <div className={prevData.modal === 3 ? 'especialFields' : ''}>
                {prevData.modal === 3 ? (
                  <>
                    <div className="fields">
                      <label className="especialLabel">Modelo</label>
                      <Select
                        value={values.id_modelo}
                        selected={
                          values.id_modelo === ''
                            ? null
                            : modelosCiclomotores
                                .filter((m) => m.id === values.id_modelo)
                                .map((item) => ({...item, label: item.nome, value: item.id}))?.[0]
                        }
                        disabled={values.id_fabricante === ''}
                        tabIndex={current === position ? 3 : -1}
                        options={modelosCiclomotores.map((item) => ({...item, label: item.nome, value: item.id}))}
                        placeholder="Clique para selecionar o modelo"
                        onBlur={() => setFieldTouched('id_modelo', true, false)}
                        onChange={({value}) => handleModelo(value)}
                      />
                      {errors?.id_modelo && <small className={'text-danger errorInput'}>{errors?.id_modelo}</small>}
                    </div>
                    <div className="fields">
                      <label className="especialLabel">{'Potência do motor em watts (W)'}</label>
                      <Select
                        value={values.id_potencia}
                        selected={
                          values.id_potencia === ''
                            ? null
                            : potenciassCiclomotores
                                .filter((m) => m.id === values.id_potencia)
                                .map((item) => ({...item, label: item.potencia, value: item.id}))?.[0]
                        }
                        disabled={potenciassCiclomotores.length === 0}
                        tabIndex={current === position ? 3 : -1}
                        options={potenciassCiclomotores.map((item) => ({...item, label: item.potencia, value: item.id}))}
                        placeholder="Clique para selecionar a potência"
                        onBlur={() => setFieldTouched('id_potencia', true, false)}
                        onChange={({value}) => handlePotencia(value)}
                      />
                      {errors?.id_potencia && <small className={'text-danger errorInput'}>{errors?.id_potencia}</small>}
                    </div>
                  </>
                ) : (
                  <div className={prevData.modal === 3 ? 'fields' : 'group'}>
                    <label className="especialLabel">Modelo</label>
                    <Input
                      ref={model}
                      nextTabRef={value}
                      placeholder={
                        prevData.modal === 0 || prevData.modal === 2
                          ? 'Qual é o modelo da usa bike?'
                          : 'Qual é o modelo do seu equipamento?'
                      }
                      name="modelo"
                      value={values.modelo}
                      onChangeText={handleChange('modelo')}
                      onBlur={handleBlur('modelo')}
                      error={errors.modelo && touched.modelo}
                    />
                    {errors?.modelo && <small className={'text-danger errorInput'}>{errors?.modelo}</small>}
                  </div>
                )}
              </div>
            </div>

            {prevData.modal === 0 ? (
              <div className="especialGroup">
                <div className="especialFields">
                  <div className="fields">
                    <label className="especialLabel">Valor de Novo da Bicicleta</label>
                    <Input
                      ref={value}
                      nextTabRef={serial}
                      placeholder="Qual é o valor de novo da sua bike?"
                      name="valor"
                      inputMode="numeric"
                      value={values.valor}
                      onChangeText={handleChange('valor')}
                      onBlur={handleBlur('valor')}
                      error={errors.valor && touched.valor}
                      formatText={Mask.money.value}
                      type="tel"
                    />
                    {errors?.valor && <small className={'text-danger errorInput'}>{errors?.valor}</small>}
                  </div>

                  <div className="fields radioFields">
                    <label className="especialLabel">Categoria</label>
                    <div className="radioFields">
                      <input
                        type="radio"
                        tabIndex={-1}
                        value="convencional"
                        checked={values.categoria !== 'eletrica' ? true : false}
                        onClick={(e) => setFieldValue('categoria', e.target.value)}
                        onChange={handleChange}
                        name="categoria"
                        id="convencional"
                      />
                      <label htmlFor="convencional">Convencional</label>
                      <input
                        type="radio"
                        tabIndex={-1}
                        name="categoria"
                        checked={values.categoria === 'eletrica' ? true : false}
                        onClick={(e) => setFieldValue('categoria', e.target.value)}
                        onChange={handleChange}
                        value="eletrica"
                        id="eletrica"
                      />
                      <label htmlFor="eletrica">Elétrica</label>
                      {errors?.categoria && <small className={'text-danger errorInput'}>{errors?.categoria}</small>}
                    </div>
                  </div>
                </div>
                <div className="alertField">
                  <Icons className="mr-2" name="informative" size="6%" color="#5A5A5A" />
                  <p>
                    O valor de novo se refere ao valor de uma bicicleta 0km no site do fabricante, independente do seu valor na
                    Nota Fiscal.
                  </p>
                </div>
              </div>
            ) : prevData.modal === 1 ? (
              <>
                <div className="group">
                  <label className="especialLabel">Valor de Novo do equipamento</label>
                  <Input
                    ref={value}
                    nextTabRef={serial}
                    placeholder="Qual é o valor de novo do equipamento?"
                    name="valor"
                    inputMode="numeric"
                    value={values.valor}
                    onChangeText={handleChange('valor')}
                    onBlur={handleBlur('valor')}
                    error={errors.valor && touched.valor}
                    formatText={Mask.money.value}
                    type="tel"
                  />
                  {errors?.valor && <small className={'text-danger errorInput'}>{errors?.valor}</small>}
                </div>

                <div className="especialGroup">
                  <div className="alertField">
                    <Icons className="mr-2" name="informative" size="6%" color="#5A5A5A" />
                    <p>
                      O valor de novo se refere ao valor de um equipamento 0km no site do fabricante, independente do seu valor na
                      Nota Fiscal.
                    </p>
                  </div>
                </div>
              </>
            ) : prevData.modal === 2 ? (
              <div className="especialGroup">
                <div className="especialFields">
                  <div className="fields">
                    <label className="especialLabel">Valor de Novo da Bicicleta</label>
                    <Input
                      ref={value}
                      nextTabRef={serial}
                      placeholder="Qual é o valor de novo da sua bike?"
                      name="valor"
                      inputMode="numeric"
                      value={values.valor}
                      onChangeText={handleChange('valor')}
                      onBlur={handleBlur('valor')}
                      error={errors.valor && touched.valor}
                      formatText={Mask.money.value}
                      type="tel"
                    />
                    {errors?.valor && <small className={'text-danger errorInput'}>{errors?.valor}</small>}
                  </div>

                  <div className="fields radioFields">
                    <label className="especialLabel">Categoria</label>
                    <div className="radioFields">
                      <input
                        type="radio"
                        tabIndex={-1}
                        value="convencional"
                        checked={values.categoria !== 'eletrica' ? true : false}
                        onClick={(e) => setFieldValue('categoria', e.target.value)}
                        onChange={handleChange}
                        name="categoria"
                        id="convencional"
                      />
                      <label htmlFor="convencional">Convencional</label>
                      <input
                        type="radio"
                        tabIndex={-1}
                        name="categoria"
                        checked={values.categoria === 'eletrica' ? true : false}
                        onClick={(e) => setFieldValue('categoria', e.target.value)}
                        onChange={handleChange}
                        value="eletrica"
                        id="eletrica"
                      />
                      <label htmlFor="eletrica">Elétrica</label>
                      {errors?.categoria && <small className={'text-danger errorInput'}>{errors?.categoria}</small>}
                    </div>
                  </div>
                </div>
                <div>
                  <p>
                    <div
                      className="fields radioFields"
                      style={{
                        marginLeft: '.9rem',
                        marginTop: '1.5rem',
                        marginBottom: '-1.5rem',
                        fontSize: '12px',
                        fontWeight: 'bold',
                      }}>
                      <label className="especialLabel">LMI a ser contratado:</label>

                      {['70', '80', '90', '100', '110', '120'].map((lmi) => (
                        <>
                          <input
                            disabled={
                              values.valor
                                ? Mask.money.raw(values.valor) * (lmi / 100) < 1000 ||
                                  Mask.money.raw(values.valor) * (lmi / 100) > 6000
                                : true
                            }
                            style={{marginLeft: '1rem', marginRight: '.3rem'}}
                            type="radio"
                            tabIndex={-1}
                            value={lmi}
                            checked={values.lmi_contratado === lmi ? true : false}
                            onClick={(e) => setFieldValue('lmi_contratado', e.target.value)}
                            onChange={handleChange}
                            name="lmi_contratado"
                            id={lmi}
                          />
                          <label htmlFor={lmi}>{lmi}%</label>
                        </>
                      ))}

                      {errors?.categoria && <small className={'text-danger errorInput'}>{errors?.categoria}</small>}
                    </div>
                  </p>
                </div>
                <div className="especialGroup">
                  <div className="alertField">
                    <Icons className="mr-2" name="informative" size="6%" color="#5A5A5A" />
                    <div>
                      <p>
                        O valor da Bicicleta pode ser considerado o valor da Nota Fiscal Eletônica, desde que, o valor de novo da
                        Bicicleta não ultrapasse R$5.000,00.
                      </p>
                      <p>O Segurado poderá optar pela contratação entre 70% a 120% do valor da Nota Fiscal Eletrônica.</p>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <>
                <div className="fields">
                  <label className="especialLabel">{'Valor de referência (Sura) para a reposição do equipamento'}</label>
                  <Input
                    disabled={true}
                    ref={value}
                    nextTabRef={serial}
                    placeholder="Valor de referência (Sura) para a reposição do equipamento"
                    name="valor"
                    inputMode="numeric"
                    value={values.valor}
                    onChangeText={handleChange('valor')}
                    onBlur={handleBlur('valor')}
                    error={errors.valor && touched.valor}
                    formatText={Mask.money.value}
                    type="tel"
                  />
                  {errors?.valor && <small className={'text-danger errorInput'}>{errors?.valor}</small>}
                </div>

                <div>
                  <p>
                    <div
                      className="fields radioFields"
                      style={{
                        marginLeft: '.9rem',
                        marginTop: '1.5rem',
                        marginBottom: '-1.5rem',
                        fontSize: '12px',
                        fontWeight: 'bold',
                      }}>
                      <label className="especialLabel">LMI a ser contratado:</label>

                      {['70', '80', '90', '100', '110', '120'].map((lmi) => (
                        <>
                          <input
                            disabled={
                              values.valor
                                ? Mask.money.raw(values.valor) * (lmi / 100) < 3000 ||
                                  Mask.money.raw(values.valor) * (lmi / 100) > 50000
                                : true
                            }
                            style={{marginLeft: '1rem', marginRight: '.3rem'}}
                            type="radio"
                            tabIndex={-1}
                            value={lmi}
                            checked={values.lmi_contratado === lmi ? true : false}
                            onClick={(e) => setFieldValue('lmi_contratado', e.target.value)}
                            onChange={handleChange}
                            name="lmi_contratado"
                            id={lmi}
                          />
                          <label htmlFor={lmi}>{lmi}%</label>
                        </>
                      ))}

                      {errors?.categoria && <small className={'text-danger errorInput'}>{errors?.categoria}</small>}
                    </div>
                  </p>
                </div>
                <div className="especialGroup">
                  <div className="alertField">
                    <Icons className="mr-2" name="informative" size="6%" color="#5A5A5A" />
                    <div>
                      <p>
                        Defina o percentual do LMI a ser contrato em relação ao valor de referência do equipamento. IMPORTANTE: em
                        quaisquer circunstância o LMI representa o valor máximo de responsabilidade da seguradora, caso o valor do
                        LMI definido seja insuficiente para a reposição do equipamento, correrá por conta do segurado o
                        complemento à indenização.
                      </p>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </form>
  );
};

export default React.memo(Plano);
export const ref = React.createRef();
